import React, { useState, useEffect } from "react"; // Added useEffect
import Loader from "../../../Common/Loader/loader";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDataTable from "../../../Common/ReportTable/ReportTable";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { formattedDate, getCurrentDateTime1 } from "../../../Common/helper/helpar";

const GvpInnerCircleReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { data, formatDate, shiftName } = location.state || {};
  const navigate = useNavigate();
  const [gvpList, setGvpList] = useState([]);

  // Transform data to recalculate clearedPercentage
  useEffect(() => {
    if (data) {
      const transformedData = data.map((item) => ({
        ...item,
        clearedPercentage: item.existingCP > 0 ? Math.round((item.clearedCP / item.existingCP) * 100) : 0,
      }));
      setGvpList(transformedData);
    }
  }, [data]);

  const scvReportColumns = [
    {
      name: "S.No",
      width: "100px",
      selector: (row) => row["sno"],
      sortable: true,
      format: (row, rowIndex) => rowIndex + 1,
    },
    {
      name: "Circle",
      selector: (row) => row["circleName"],
      cell: (row) => <span>{row["circleName"]}</span>,
      sortable: true,
      width: "130px",
    },
    {
      name: "No. of Existing GVPs",
      selector: (row) => row["existingCP"],
      width: "200px",
      cell: (row) => <span>{row["existingCP"]}</span>,
      sortable: true,
    },
    {
      name: "GVPs Cleared",
      selector: (row) => row["clearedCP"],
      cell: (row) => <span>{row["clearedCP"]}</span>,
      sortable: true,
    },
    {
      name: "GVPs Cleared %",
      selector: (row) => row["clearedPercentage"],
      cell: (row) => <span>{row["clearedPercentage"]}%</span>, // Append % symbol
      sortable: true,
    },
    {
      name: "Cleared HotSpots",
      selector: (row) => row["clearedHotspot"],
      cell: (row) => <span>{row["clearedHotspot"]}</span>,
      sortable: true,
    },
    {
      name: "Total Cleared",
      selector: (row) => row["totalCleared"],
      cell: (row) => <span>{row["totalCleared"]}</span>,
      sortable: true,
    },
  ];

  const downloadAsExcel = async () => {
    const currentDate = new Date();
    const formattedDates = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const cumulativeHeadingReport = `GVPs Circle Wise Report as on ${formattedDate(formatDate)} - ${shiftName} - (at ${getCurrentDateTime1()})`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Circles"); // Changed worksheet name to "Circles" for clarity

    const boldFont = { bold: true };

    const downloadDateTime = `Reported on: ${formattedDates} ${formattedTime}`;
    const dateTimeRow = worksheet.addRow(["", "", downloadDateTime, "", ""]);
    worksheet.mergeCells(1, 3, 1, 5);
    dateTimeRow.getCell(3).font = boldFont;
    dateTimeRow.getCell(3).alignment = { horizontal: "center" };
    dateTimeRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber >= 3 && colNumber <= 5) {
        cell.font = boldFont;
        cell.alignment = { horizontal: "center" };
      }
    });

    const cumulativeHeadingRow = worksheet.addRow(["", "", cumulativeHeadingReport, "", ""]);
    worksheet.mergeCells(2, 3, 2, 5);
    cumulativeHeadingRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber >= 3 && colNumber <= 5) {
        cell.font = boldFont;
        cell.alignment = { horizontal: "center" };
      }
    });

    worksheet.addRow([""]);

    const tableHeaderRow = worksheet.addRow([
      "S.No",
      "Circle",
      "No Of Existing GVPs",
      "GVPs Cleared",
      "GVPs Cleared %",
      "Cleared HotSpots",
      "Total Cleared",
    ]);

    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "left" };
      if (colNumber >= 1 && colNumber <= 7) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" },
        };
      }
    });

    gvpList.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        item.circleName ?? "N/A",
        item.existingCP ?? "N/A",
        item.clearedCP ?? "N/A",
        item.clearedPercentage ? `${item.clearedPercentage}%` : "N/A", // Append % symbol
        item.clearedHotspot ?? "N/A",
        item.totalCleared ?? "N/A",
      ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.alignment = { horizontal: "left" };
      });
    });

    worksheet.columns = [
      { width: 5 },
      { width: 20 },
      { width: 20 },
      { width: 20 },
      { width: 25 },
      { width: 20 },
      { width: 20 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "GVPsMultipleClearanceCircleWiseReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const searchGVPCircleHandler = (e) => {
    const term = e.target.value.toLowerCase();
    const filteredData = data.filter((item) =>
      item?.circleName?.toLowerCase()?.includes(term)
    );
    // Recalculate clearedPercentage for filtered data
    const transformedData = filteredData.map((item) => ({
      ...item,
      clearedPercentage: item.existingCP > 0 ? Math.round((item.clearedCP / item.existingCP) * 100) : 0,
    }));
    setGvpList(term ? transformedData : transformedData);
  };

  return (
    <>
      <div style={{ width: "100%" }}>
        <div className="content">
          <div className="page-content">
            <div className="card mt-3">
              <div className="card-body">
                <div className="page-header">
                  <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                    <div className="col-md-3">
                      <div className="form-group">
                        <input
                          placeholder="Search Circle"
                          type="text"
                          className="form-control bg-white"
                          onInput={searchGVPCircleHandler}
                        />
                      </div>
                    </div>
                    <div className="d-flex">
                      <button
                        className="btn btn-secondary me-2"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>
                      <button className="btn btn-excel" onClick={downloadAsExcel}>
                        <i className="fa-solid fa-file-excel me-1 icon-container"></i> Excel
                      </button>
                    </div>
                  </div>
                </div>
                <div className="top-header">
                  <span>
                    GVPs Circle Wise Report {formattedDate(formatDate)} - {shiftName} - (at {getCurrentDateTime1()})
                  </span>
                </div>

                <CustomDataTable data={gvpList} columns={scvReportColumns} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isLoading && <Loader />}
    </>
  );
};

export default GvpInnerCircleReport;